import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Package, Users, DollarSign, Truck, Clock, CheckCircle, XCircle, Search, ChevronDown, ExternalLink, Mail, Download, Send, Bell, Megaphone, Star, Trash2, Sparkles, Boxes, Plus, Save, Edit3, Hammer } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  
  // Update form
  const [newStatus, setNewStatus] = useState("");
  const [trackingNumber, setTrackingNumber] = useState("");
  const [courier, setCourier] = useState("");

  // Subscribers tab
  const [activeTab, setActiveTab] = useState("orders"); // "orders" | "subscribers" | "reviews" | "bundles"
  const [subscribers, setSubscribers] = useState([]);
  const [subsStats, setSubsStats] = useState({ total: 0, claimed: 0, unclaimed: 0 });
  const [subsLoading, setSubsLoading] = useState(false);
  const [subsSearch, setSubsSearch] = useState("");

  // PVC waitlist (compact panel in subscribers tab)
  const [pvcWaitlist, setPvcWaitlist] = useState({ total: 0, pending: 0 });
  const [broadcasting, setBroadcasting] = useState(false);

  // Reviews tab
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [deletingReviewId, setDeletingReviewId] = useState(null);
  const [cleaningTestData, setCleaningTestData] = useState(false);

  // Bundles tab
  const [bundles, setBundles] = useState([]);
  const [bundlesLoading, setBundlesLoading] = useState(false);
  const [bundleDraft, setBundleDraft] = useState({}); // keyed by bundle id — holds unsaved edits
  const [savingBundleId, setSavingBundleId] = useState(null);
  const [reseeding, setReseeding] = useState(false);

  // Workshop tab
  const [workshop, setWorkshop] = useState(null);
  const [workshopDraft, setWorkshopDraft] = useState("");
  const [workshopSaving, setWorkshopSaving] = useState(false);
  const [sendingBuildEmail, setSendingBuildEmail] = useState(null); // `${orderId}:${stage}`

  const fetchWorkshop = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/workshop-settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setWorkshop(data);
      setWorkshopDraft(String(data.monthly_capacity ?? ""));
    } catch {
      toast.error("Failed to load workshop settings");
    }
  };

  const saveWorkshop = async () => {
    const cap = parseInt(workshopDraft, 10);
    if (!cap || cap < 1 || cap > 500) {
      toast.error("Capacity must be between 1 and 500");
      return;
    }
    setWorkshopSaving(true);
    try {
      const { data } = await axios.put(
        `${API}/admin/workshop-settings`,
        { monthly_capacity: cap },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Monthly capacity set to ${data.monthly_capacity}`);
      await fetchWorkshop();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setWorkshopSaving(false);
    }
  };

  const sendBuildEmail = async (orderId, stage) => {
    const key = `${orderId}:${stage}`;
    setSendingBuildEmail(key);
    try {
      const { data } = await axios.post(
        `${API}/admin/orders/${orderId}/send-build-email`,
        { stage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (data.status === "already_sent") {
        toast.info(`${stage.replace("_", " ")} email already sent for this order`);
      } else {
        toast.success(`${stage.replace("_", " ")} email delivered`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send build email");
    } finally {
      setSendingBuildEmail(null);
    }
  };

  const fetchBundles = async () => {
    setBundlesLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/bundles`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBundles(data.bundles || []);
      setBundleDraft({});
    } catch {
      toast.error("Failed to load bundles");
    } finally {
      setBundlesLoading(false);
    }
  };

  const patchDraft = (id, patch) => {
    setBundleDraft((prev) => {
      const base = prev[id] ?? bundles.find((b) => b.id === id);
      return { ...prev, [id]: { ...base, ...patch } };
    });
  };

  const patchItem = (id, idx, patch) => {
    const base = bundleDraft[id] ?? bundles.find((b) => b.id === id);
    const items = base.items.map((it, i) => (i === idx ? { ...it, ...patch } : it));
    patchDraft(id, { items });
  };

  const addItem = (id) => {
    const base = bundleDraft[id] ?? bundles.find((b) => b.id === id);
    patchDraft(id, { items: [...base.items, { name: "New item", supplier_price: 0 }] });
  };

  const removeItem = (id, idx) => {
    const base = bundleDraft[id] ?? bundles.find((b) => b.id === id);
    patchDraft(id, { items: base.items.filter((_, i) => i !== idx) });
  };

  const saveBundle = async (id) => {
    const draft = bundleDraft[id];
    if (!draft) return;
    setSavingBundleId(id);
    try {
      await axios.put(`${API}/admin/bundles/${id}`, {
        id,
        name: draft.name,
        tagline: draft.tagline,
        items: draft.items,
        total: Number(draft.total),
        rrp_total: Number(draft.rrp_total),
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Bundle saved");
      fetchBundles();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSavingBundleId(null);
    }
  };

  const deleteBundle = async (id, name) => {
    if (!window.confirm(`Delete the "${name}" bundle permanently?`)) return;
    try {
      await axios.delete(`${API}/admin/bundles/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Bundle deleted");
      fetchBundles();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const reseedFromConfig = async () => {
    if (!window.confirm("Reseed ALL bundles from code defaults? Your admin edits will be LOST.")) return;
    setReseeding(true);
    try {
      const { data } = await axios.post(`${API}/admin/bundles/reseed`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Reseeded ${data.reseeded} bundles from defaults`);
      fetchBundles();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reseed failed");
    } finally {
      setReseeding(false);
    }
  };

  const fetchReviews = async () => {
    setReviewsLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/reviews`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReviews(data.reviews || []);
    } catch (error) {
      toast.error("Failed to load reviews");
    } finally {
      setReviewsLoading(false);
    }
  };

  const deleteReview = async (reviewId, title) => {
    if (!window.confirm(`Permanently delete this review?\n\n"${title}"`)) return;
    setDeletingReviewId(reviewId);
    try {
      await axios.delete(`${API}/admin/reviews/${reviewId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Review deleted");
      setReviews((prev) => prev.filter((r) => r.id !== reviewId));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete review");
    } finally {
      setDeletingReviewId(null);
    }
  };

  const cleanupTestReviews = async () => {
    if (!window.confirm("Find and delete all auto-generated TEST reviews (titles starting with TEST/Playwright, or user names like 'Test ...')?")) return;
    setCleaningTestData(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/reviews/cleanup-test-data`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Removed ${data.deleted_count} test review(s)`);
      fetchReviews();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Cleanup failed");
    } finally {
      setCleaningTestData(false);
    }
  };

  const fetchPVCWaitlist = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/pvc-waitlist`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPvcWaitlist({ total: data.total, pending: data.pending });
    } catch (error) { /* silent — panel just shows 0 */ }
  };

  const broadcastPVCRestock = async () => {
    if (!window.confirm(`Send "PVC back in stock" email to ${pvcWaitlist.pending} subscriber(s)?`)) return;
    setBroadcasting(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/pvc-waitlist/broadcast`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Broadcast sent to ${data.sent} of ${data.queued} subscribers`);
      fetchPVCWaitlist();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Broadcast failed");
    } finally {
      setBroadcasting(false);
    }
  };

  const fetchSubscribers = async () => {
    setSubsLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/newsletter-subscribers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSubscribers(data.subscribers || []);
      setSubsStats({ total: data.total, claimed: data.claimed, unclaimed: data.unclaimed });
    } catch (error) {
      toast.error("Failed to load subscribers");
    } finally {
      setSubsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "subscribers" && user && token) {
      fetchSubscribers();
      fetchPVCWaitlist();
    }
    if (activeTab === "reviews" && user && token) {
      fetchReviews();
    }
    if (activeTab === "bundles" && user && token) {
      fetchBundles();
    }
    if (activeTab === "workshop" && user && token) {
      fetchWorkshop();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const filteredSubs = subscribers.filter((s) => {
    if (!subsSearch) return true;
    const q = subsSearch.toLowerCase();
    return (s.email || "").toLowerCase().includes(q) || (s.code || "").toLowerCase().includes(q);
  });

  const [resendingCode, setResendingCode] = useState(null);

  const resendCode = async (code) => {
    setResendingCode(code);
    try {
      await axios.post(
        `${API}/admin/newsletter-subscribers/${code}/resend`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Welcome email re-sent`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't resend email");
    } finally {
      setResendingCode(null);
    }
  };

  const exportSubsCSV = () => {
    if (!subscribers.length) {
      toast.error("Nothing to export yet");
      return;
    }
    const headers = ["email", "code", "discount_percent", "claimed", "created_at", "claimed_at", "order_id"];
    const csvRows = [headers.join(",")];
    for (const s of subscribers) {
      const row = headers.map((h) => {
        const v = s[h] == null ? "" : String(s[h]);
        // escape quotes + wrap if contains comma
        const escaped = v.includes('"') ? v.replace(/"/g, '""') : v;
        return /[,"\n]/.test(v) ? `"${escaped}"` : escaped;
      });
      csvRows.push(row.join(","));
    }
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `repticraft-subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${subscribers.length} subscribers`);
  };

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }
    fetchAdminData();
  }, [user, navigate, statusFilter]);

  const fetchAdminData = async () => {
    try {
      const [statsRes, ordersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/orders${statusFilter ? `?status=${statusFilter}` : ''}`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setStats(statsRes.data);
      setOrders(ordersRes.data.orders);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Admin access required");
        navigate("/dashboard");
      } else {
        toast.error("Failed to load admin data");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!selectedOrder || !newStatus) return;
    
    setUpdating(true);
    try {
      await axios.put(
        `${API}/admin/orders/${selectedOrder.id}/status`,
        {
          status: newStatus,
          tracking_number: trackingNumber || null,
          courier: courier || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Order updated to ${newStatus}`);
      setSelectedOrder(null);
      setNewStatus("");
      setTrackingNumber("");
      setCourier("");
      fetchAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update order");
    } finally {
      setUpdating(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "bg-yellow-500/20 text-yellow-400",
      processing: "bg-blue-500/20 text-blue-400",
      shipped: "bg-purple-500/20 text-purple-400",
      delivered: "bg-green-500/20 text-green-400",
      cancelled: "bg-red-500/20 text-red-400"
    };
    return colors[status] || "bg-white/10 text-white/60";
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 px-4 bg-[#0A0A0A] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 px-4 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold font-['Space_Grotesk'] text-[#F4F1DE] mb-8">
          Admin Dashboard
        </h1>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-[#121212] border border-white/10 p-6">
              <div className="flex items-center gap-3 mb-2">
                <DollarSign className="w-5 h-5 text-green-400" />
                <span className="text-[#F4F1DE]/60">Revenue</span>
              </div>
              <p className="text-2xl font-bold text-green-400">£{stats.revenue.toLocaleString()}</p>
            </div>
            
            <div className="bg-[#121212] border border-white/10 p-6">
              <div className="flex items-center gap-3 mb-2">
                <Package className="w-5 h-5 text-[#E07A5F]" />
                <span className="text-[#F4F1DE]/60">Total Orders</span>
              </div>
              <p className="text-2xl font-bold text-[#F4F1DE]">{stats.orders.total}</p>
            </div>
            
            <div className="bg-[#121212] border border-white/10 p-6">
              <div className="flex items-center gap-3 mb-2">
                <Clock className="w-5 h-5 text-yellow-400" />
                <span className="text-[#F4F1DE]/60">Pending</span>
              </div>
              <p className="text-2xl font-bold text-yellow-400">{stats.orders.pending}</p>
            </div>
            
            <div className="bg-[#121212] border border-white/10 p-6">
              <div className="flex items-center gap-3 mb-2">
                <Users className="w-5 h-5 text-blue-400" />
                <span className="text-[#F4F1DE]/60">Users</span>
              </div>
              <p className="text-2xl font-bold text-blue-400">{stats.users}</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-white/10" data-testid="admin-tabs">
          <button
            onClick={() => setActiveTab("orders")}
            className={`px-4 py-3 text-sm font-semibold transition-all border-b-2 -mb-px flex items-center gap-2 ${
              activeTab === "orders"
                ? "border-[#E07A5F] text-[#E07A5F]"
                : "border-transparent text-[#F4F1DE]/50 hover:text-[#F4F1DE]/80"
            }`}
            data-testid="tab-orders"
          >
            <Package className="w-4 h-4" />
            Orders
          </button>
          <button
            onClick={() => setActiveTab("subscribers")}
            className={`px-4 py-3 text-sm font-semibold transition-all border-b-2 -mb-px flex items-center gap-2 ${
              activeTab === "subscribers"
                ? "border-[#E07A5F] text-[#E07A5F]"
                : "border-transparent text-[#F4F1DE]/50 hover:text-[#F4F1DE]/80"
            }`}
            data-testid="tab-subscribers"
          >
            <Mail className="w-4 h-4" />
            Subscribers
          </button>
          <button
            onClick={() => setActiveTab("reviews")}
            className={`px-4 py-3 text-sm font-semibold transition-all border-b-2 -mb-px flex items-center gap-2 ${
              activeTab === "reviews"
                ? "border-[#E07A5F] text-[#E07A5F]"
                : "border-transparent text-[#F4F1DE]/50 hover:text-[#F4F1DE]/80"
            }`}
            data-testid="tab-reviews"
          >
            <Star className="w-4 h-4" />
            Reviews
          </button>
          <button
            onClick={() => setActiveTab("bundles")}
            className={`px-4 py-3 text-sm font-semibold transition-all border-b-2 -mb-px flex items-center gap-2 ${
              activeTab === "bundles"
                ? "border-[#E07A5F] text-[#E07A5F]"
                : "border-transparent text-[#F4F1DE]/50 hover:text-[#F4F1DE]/80"
            }`}
            data-testid="tab-bundles"
          >
            <Boxes className="w-4 h-4" />
            Bundles
          </button>
          <button
            onClick={() => setActiveTab("workshop")}
            className={`px-4 py-3 text-sm font-semibold transition-all border-b-2 -mb-px flex items-center gap-2 ${
              activeTab === "workshop"
                ? "border-[#E07A5F] text-[#E07A5F]"
                : "border-transparent text-[#F4F1DE]/50 hover:text-[#F4F1DE]/80"
            }`}
            data-testid="tab-workshop"
          >
            <Hammer className="w-4 h-4" />
            Workshop
          </button>
        </div>

        {/* Subscribers Section */}
        {activeTab === "subscribers" && (
          <div className="space-y-6">

            {/* PVC Waitlist panel */}
            <div className="bg-gradient-to-r from-blue-500/10 to-[#121212] border border-blue-500/30 rounded p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-testid="admin-pvc-waitlist-panel">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded bg-blue-500/20 flex items-center justify-center shrink-0">
                  <Bell className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-[#F4F1DE] font-semibold">PVC Restock Waitlist</p>
                  <p className="text-sm text-[#F4F1DE]/60">
                    <span className="text-blue-300">{pvcWaitlist.pending}</span> waiting to be notified ·{" "}
                    <span className="text-[#F4F1DE]/50">{pvcWaitlist.total}</span> lifetime sign-ups
                  </p>
                </div>
              </div>
              <Button
                onClick={broadcastPVCRestock}
                disabled={broadcasting || pvcWaitlist.pending === 0}
                className="bg-blue-500 hover:bg-blue-600 text-white font-semibold gap-2"
                data-testid="pvc-broadcast-btn"
              >
                <Megaphone className="w-4 h-4" />
                {broadcasting ? "Sending..." : `Broadcast "back in stock"`}
              </Button>
            </div>

          <div className="bg-[#121212] border border-white/10 p-6" data-testid="admin-subscribers-section">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
              <div>
                <h2 className="text-xl font-bold text-[#F4F1DE]">Newsletter Subscribers</h2>
                <p className="text-sm text-[#F4F1DE]/50 mt-1">
                  <span className="text-[#F4F1DE]">{subsStats.total}</span> total ·{" "}
                  <span className="text-green-400">{subsStats.claimed}</span> redeemed ·{" "}
                  <span className="text-amber-400">{subsStats.unclaimed}</span> unused
                </p>
              </div>
              <div className="flex gap-3 w-full md:w-auto">
                <div className="relative flex-1 md:w-64">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#F4F1DE]/40" />
                  <Input
                    placeholder="Search email or code"
                    value={subsSearch}
                    onChange={(e) => setSubsSearch(e.target.value)}
                    className="pl-9 bg-[#0A0A0A] border-white/10 text-[#F4F1DE]"
                    data-testid="subscribers-search"
                  />
                </div>
                <Button
                  onClick={exportSubsCSV}
                  disabled={!subscribers.length}
                  className="bg-[#E07A5F] hover:bg-[#D06A4F] text-white gap-2"
                  data-testid="export-csv-btn"
                >
                  <Download className="w-4 h-4" />
                  Export CSV
                </Button>
              </div>
            </div>

            {subsLoading ? (
              <p className="text-[#F4F1DE]/60 py-8 text-center">Loading subscribers...</p>
            ) : filteredSubs.length === 0 ? (
              <div className="py-10 text-center border border-dashed border-white/10 rounded">
                <Mail className="w-10 h-10 text-[#F4F1DE]/30 mx-auto mb-2" />
                <p className="text-[#F4F1DE]/60">
                  {subsSearch ? "No subscribers match your search." : "No subscribers yet — the newsletter popup will fill this up."}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="subscribers-table">
                  <thead>
                    <tr className="border-b border-white/10 text-[#F4F1DE]/50 text-left">
                      <th className="py-2 px-3 font-semibold">Email</th>
                      <th className="py-2 px-3 font-semibold">Code</th>
                      <th className="py-2 px-3 font-semibold">Discount</th>
                      <th className="py-2 px-3 font-semibold">Status</th>
                      <th className="py-2 px-3 font-semibold">Subscribed</th>
                      <th className="py-2 px-3 font-semibold">Claimed</th>
                      <th className="py-2 px-3 font-semibold text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSubs.map((s) => (
                      <tr key={s.id} className="border-b border-white/5 text-[#F4F1DE]/80">
                        <td className="py-3 px-3">{s.email}</td>
                        <td className="py-3 px-3 font-mono text-[#E07A5F]">{s.code}</td>
                        <td className="py-3 px-3">{s.discount_percent}%</td>
                        <td className="py-3 px-3">
                          {s.claimed ? (
                            <span className="px-2 py-0.5 text-xs rounded bg-green-500/10 border border-green-500/30 text-green-400">
                              Redeemed
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 text-xs rounded bg-amber-500/10 border border-amber-500/30 text-amber-400">
                              Unused
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-3 text-xs text-[#F4F1DE]/60">
                          {s.created_at ? new Date(s.created_at).toLocaleDateString() : "—"}
                        </td>
                        <td className="py-3 px-3 text-xs text-[#F4F1DE]/60">
                          {s.claimed_at ? new Date(s.claimed_at).toLocaleDateString() : "—"}
                        </td>
                        <td className="py-3 px-3 text-right">
                          <button
                            onClick={() => resendCode(s.code)}
                            disabled={resendingCode === s.code || s.claimed}
                            title={s.claimed ? "Code already redeemed" : "Resend welcome email"}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs border border-white/15 rounded text-[#F4F1DE]/70 hover:text-[#E07A5F] hover:border-[#E07A5F]/40 transition-colors disabled:opacity-30 disabled:hover:text-[#F4F1DE]/70 disabled:hover:border-white/15 disabled:cursor-not-allowed"
                            data-testid={`resend-${s.code}`}
                          >
                            <Send className="w-3 h-3" />
                            {resendingCode === s.code ? "Sending..." : "Resend"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          </div>
        )}

        {/* Reviews Section */}
        {activeTab === "reviews" && (
          <div className="space-y-6" data-testid="admin-reviews-section">
            {/* Cleanup test data panel */}
            <div className="bg-gradient-to-r from-amber-500/10 to-[#121212] border border-amber-500/30 rounded p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-testid="admin-test-data-cleanup-panel">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded bg-amber-500/20 flex items-center justify-center shrink-0">
                  <Sparkles className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-amber-400 uppercase tracking-wider">Test data cleanup</p>
                  <p className="text-sm text-[#F4F1DE]/80 mt-1">Removes auto-generated reviews left over from QA runs (titles starting with TEST/Playwright, or user names beginning "Test ").</p>
                </div>
              </div>
              <Button
                onClick={cleanupTestReviews}
                disabled={cleaningTestData}
                className="bg-amber-500 hover:bg-amber-600 text-black font-semibold whitespace-nowrap"
                data-testid="cleanup-test-reviews-btn"
              >
                {cleaningTestData ? "Cleaning..." : "Remove test reviews"}
              </Button>
            </div>

            {/* Reviews list */}
            <div className="bg-[#121212] border border-white/10 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-[#F4F1DE]">All Reviews ({reviews.length})</h2>
              </div>

              {reviewsLoading ? (
                <div className="flex justify-center py-10"><div className="spinner" /></div>
              ) : reviews.length === 0 ? (
                <p className="text-[#F4F1DE]/50 text-center py-10">No reviews yet.</p>
              ) : (
                <div className="space-y-3" data-testid="admin-reviews-list">
                  {reviews.map((r) => (
                    <div
                      key={r.id}
                      className="border border-white/10 rounded p-4 hover:border-white/20 transition-colors"
                      data-testid={`admin-review-${r.id}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-1.5">
                            <span className="font-semibold text-[#F4F1DE]">{r.user_name}</span>
                            <span className="text-xs px-1.5 py-0.5 bg-white/5 text-[#F4F1DE]/60 uppercase tracking-wider rounded">{r.product_type}</span>
                            <span className="flex items-center gap-0.5 text-amber-400 text-xs">
                              {Array.from({ length: r.rating }).map((_, i) => (
                                <Star key={i} className="w-3 h-3 fill-amber-400" />
                              ))}
                            </span>
                            {r.verified_purchase && (
                              <span className="text-[10px] px-1.5 py-0.5 bg-green-500/15 text-green-400 uppercase tracking-wider rounded">Verified</span>
                            )}
                            <span className="text-xs text-[#F4F1DE]/40">
                              {new Date(r.created_at).toLocaleDateString("en-GB")}
                            </span>
                          </div>
                          <p className="text-sm font-semibold text-[#F4F1DE]/90 mb-1">{r.title}</p>
                          <p className="text-sm text-[#F4F1DE]/60 leading-relaxed">{r.content}</p>
                        </div>
                        <Button
                          onClick={() => deleteReview(r.id, r.title)}
                          disabled={deletingReviewId === r.id}
                          variant="outline"
                          size="sm"
                          className="border-red-500/30 text-red-400 hover:bg-red-500/10 hover:border-red-500/50 shrink-0"
                          data-testid={`delete-review-${r.id}`}
                        >
                          <Trash2 className="w-4 h-4 mr-1.5" />
                          {deletingReviewId === r.id ? "..." : "Delete"}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Bundles Section */}
        {activeTab === "bundles" && (
          <div className="space-y-6" data-testid="admin-bundles-section">
            {/* Header / reseed */}
            <div className="bg-[#121212] border border-white/10 p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-amber-400 uppercase tracking-wider">Equipment bundles</p>
                <p className="text-sm text-[#F4F1DE]/70 mt-1">
                  These kits appear as upsells on every configurator. Totals feed through to cart and checkout directly.
                  Source of truth is MongoDB — "reseed from defaults" reverts everything to the hardcoded values in <code className="text-[#E07A5F]">config.py</code>.
                </p>
              </div>
              <Button
                onClick={reseedFromConfig}
                disabled={reseeding}
                variant="outline"
                className="border-amber-500/40 text-amber-400 hover:bg-amber-500/10 whitespace-nowrap"
                data-testid="reseed-bundles-btn"
              >
                {reseeding ? "Reseeding..." : "Reseed from defaults"}
              </Button>
            </div>

            {/* Bundle list */}
            {bundlesLoading ? (
              <div className="flex justify-center py-10"><div className="spinner" /></div>
            ) : bundles.length === 0 ? (
              <div className="bg-[#121212] border border-white/10 p-10 text-center">
                <p className="text-[#F4F1DE]/60 mb-3">No bundles in the database yet.</p>
                <Button onClick={reseedFromConfig} className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="reseed-empty-btn">
                  Seed from code defaults
                </Button>
              </div>
            ) : (
              bundles.map((b) => {
                const draft = bundleDraft[b.id] ?? b;
                const isDirty = !!bundleDraft[b.id];
                const itemsTotal = draft.items.reduce((s, i) => s + Number(i.supplier_price || 0), 0);
                return (
                  <div
                    key={b.id}
                    className="bg-[#121212] border border-white/10 rounded"
                    data-testid={`admin-bundle-${b.id}`}
                  >
                    {/* Bundle header */}
                    <div className="p-4 border-b border-white/10 flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                      <div className="flex-1 min-w-0 space-y-2">
                        <div>
                          <Label className="text-[10px] uppercase tracking-widest text-[#F4F1DE]/50">Bundle name</Label>
                          <Input
                            value={draft.name}
                            onChange={(e) => patchDraft(b.id, { name: e.target.value })}
                            className="bg-[#0A0A0A] border-white/10 text-[#F4F1DE] text-base font-semibold mt-1"
                            data-testid={`bundle-name-${b.id}`}
                          />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase tracking-widest text-[#F4F1DE]/50">Tagline</Label>
                          <Input
                            value={draft.tagline}
                            onChange={(e) => patchDraft(b.id, { tagline: e.target.value })}
                            className="bg-[#0A0A0A] border-white/10 text-[#F4F1DE]/70 text-sm mt-1"
                            data-testid={`bundle-tagline-${b.id}`}
                          />
                        </div>
                      </div>
                      <div className="flex gap-2 md:w-[270px] shrink-0">
                        <div className="flex-1">
                          <Label className="text-[10px] uppercase tracking-widest text-green-400">Sell price (£)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={draft.total}
                            onChange={(e) => patchDraft(b.id, { total: e.target.value })}
                            className="bg-[#0A0A0A] border-green-500/30 text-green-400 font-bold mt-1"
                            data-testid={`bundle-total-${b.id}`}
                          />
                        </div>
                        <div className="flex-1">
                          <Label className="text-[10px] uppercase tracking-widest text-[#F4F1DE]/50">RRP (£)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={draft.rrp_total}
                            onChange={(e) => patchDraft(b.id, { rrp_total: e.target.value })}
                            className="bg-[#0A0A0A] border-white/10 text-[#F4F1DE]/70 mt-1"
                            data-testid={`bundle-rrp-${b.id}`}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Items editor */}
                    <div className="p-4 space-y-2">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs uppercase tracking-widest text-[#F4F1DE]/50">
                          Items ({draft.items.length}) — supplier total £{itemsTotal.toFixed(2)}
                        </p>
                        <button
                          onClick={() => addItem(b.id)}
                          className="flex items-center gap-1 text-xs text-[#E07A5F] hover:text-[#F5A593]"
                          data-testid={`add-item-${b.id}`}
                        >
                          <Plus className="w-3.5 h-3.5" /> Add item
                        </button>
                      </div>
                      {draft.items.map((it, idx) => (
                        <div key={idx} className="flex gap-2 items-center" data-testid={`bundle-${b.id}-item-${idx}`}>
                          <Input
                            value={it.name}
                            onChange={(e) => patchItem(b.id, idx, { name: e.target.value })}
                            className="flex-1 bg-[#0A0A0A] border-white/10 text-sm text-[#F4F1DE]/90"
                            placeholder="Item name"
                          />
                          <Input
                            type="number"
                            step="0.01"
                            value={it.supplier_price}
                            onChange={(e) => patchItem(b.id, idx, { supplier_price: Number(e.target.value) })}
                            className="w-24 bg-[#0A0A0A] border-white/10 text-sm text-[#F4F1DE]/90 tabular-nums"
                            placeholder="£"
                          />
                          <button
                            onClick={() => removeItem(b.id, idx)}
                            className="text-red-400 hover:bg-red-500/10 rounded px-2 py-1"
                            data-testid={`remove-item-${b.id}-${idx}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>

                    {/* Footer actions */}
                    <div className="p-4 border-t border-white/10 flex items-center justify-between gap-3">
                      <button
                        onClick={() => deleteBundle(b.id, b.name)}
                        className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1"
                        data-testid={`delete-bundle-${b.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Delete bundle
                      </button>
                      <Button
                        onClick={() => saveBundle(b.id)}
                        disabled={!isDirty || savingBundleId === b.id}
                        className="bg-[#E07A5F] hover:bg-[#D06A4F] text-white disabled:opacity-40"
                        data-testid={`save-bundle-${b.id}`}
                      >
                        <Save className="w-4 h-4 mr-1.5" />
                        {savingBundleId === b.id ? "Saving..." : isDirty ? "Save changes" : "No changes"}
                      </Button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}


        {/* Orders Section */}
        {activeTab === "orders" && (
        <div className="bg-[#121212] border border-white/10 p-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
            <h2 className="text-xl font-bold text-[#F4F1DE]">Orders</h2>
            
            {/* Status Filter */}
            <div className="flex gap-2 flex-wrap">
              {["", "pending", "processing", "shipped", "delivered"].map((status) => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={`px-3 py-1 text-sm rounded transition-colors ${
                    statusFilter === status
                      ? 'bg-[#E07A5F] text-white'
                      : 'bg-white/5 text-[#F4F1DE]/60 hover:bg-white/10'
                  }`}
                >
                  {status || "All"}
                </button>
              ))}
            </div>
          </div>

          {/* Orders Table */}
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="admin-orders-table">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-3 px-4 text-[#F4F1DE]/60 font-medium">Order ID</th>
                  <th className="text-left py-3 px-4 text-[#F4F1DE]/60 font-medium">Date</th>
                  <th className="text-left py-3 px-4 text-[#F4F1DE]/60 font-medium">Total</th>
                  <th className="text-left py-3 px-4 text-[#F4F1DE]/60 font-medium">Status</th>
                  <th className="text-left py-3 px-4 text-[#F4F1DE]/60 font-medium">Tracking</th>
                  <th className="text-right py-3 px-4 text-[#F4F1DE]/60 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order.id} className="border-b border-white/5 hover:bg-white/5">
                    <td className="py-3 px-4 text-[#F4F1DE] font-mono text-sm">
                      #{order.id.slice(0, 8).toUpperCase()}
                    </td>
                    <td className="py-3 px-4 text-[#F4F1DE]/70 text-sm">
                      {new Date(order.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4 text-[#E07A5F] font-bold">
                      £{order.total?.toFixed(2)}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 text-xs rounded ${getStatusColor(order.status)}`}>
                        {order.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-[#F4F1DE]/60 text-sm">
                      {order.tracking_number ? (
                        <span className="font-mono">{order.tracking_number}</span>
                      ) : (
                        <span className="text-[#F4F1DE]/30">—</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <BuildEmailMenu
                          order={order}
                          sending={sendingBuildEmail}
                          onSend={sendBuildEmail}
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedOrder(order);
                            setNewStatus(order.status);
                            setTrackingNumber(order.tracking_number || "");
                            setCourier(order.courier || "");
                          }}
                          className="border-white/20 text-[#F4F1DE]/80"
                          data-testid={`update-order-${order.id.slice(0,8)}`}
                        >
                          Update
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {orders.length === 0 && (
            <p className="text-center text-[#F4F1DE]/40 py-8">No orders found</p>
          )}
        </div>
        )}

        {/* Workshop Tab */}
        {activeTab === "workshop" && (
          <div className="space-y-6" data-testid="admin-workshop-panel">
            <div className="flex items-center gap-3">
              <Hammer className="w-5 h-5 text-[#E07A5F]" />
              <h2 className="text-xl font-bold text-[#F4F1DE]">Workshop capacity</h2>
            </div>

            <div className="bg-[#121212] border border-white/10 p-6 max-w-2xl">
              <p className="text-sm text-[#F4F1DE]/70 leading-relaxed mb-4">
                Monthly build capacity powers the <strong>"slots left before dispatch"</strong>
                counter on the homepage and the queue position shown on order confirmation pages.
                Keep it honest — set it to the real number of enclosures your workshop can ship in
                a calendar month.
              </p>

              <label className="block text-[11px] uppercase tracking-widest text-[#F4F1DE]/50 mb-2 font-semibold">
                Monthly capacity
              </label>
              <div className="flex gap-3 items-start">
                <Input
                  type="number"
                  min="1"
                  max="500"
                  value={workshopDraft}
                  onChange={(e) => setWorkshopDraft(e.target.value)}
                  className="w-32 bg-[#0A0A0A] border-white/10 text-[#F4F1DE]"
                  data-testid="workshop-capacity-input"
                />
                <Button
                  onClick={saveWorkshop}
                  disabled={workshopSaving || workshopDraft === String(workshop?.monthly_capacity ?? "")}
                  className="bg-[#E07A5F] hover:bg-[#D06A4F] text-white font-semibold"
                  data-testid="workshop-capacity-save"
                >
                  <Save className="w-4 h-4 mr-1.5" />
                  {workshopSaving ? "Saving..." : "Save"}
                </Button>
              </div>

              {workshop && (
                <div className="mt-5 text-xs text-[#F4F1DE]/50 space-y-1">
                  <p>Current value: <strong className="text-[#F4F1DE]">{workshop.monthly_capacity}</strong> builds / month ({workshop.source === "db" ? "admin-set" : "config default"}).</p>
                  <p>Env default: <code className="text-[#F4F1DE]/70">{workshop.default_monthly_capacity}</code></p>
                  {workshop.updated_at && (
                    <p>Last changed: {new Date(workshop.updated_at).toLocaleString()}</p>
                  )}
                </div>
              )}
            </div>

            <div className="bg-[#121212] border border-white/10 p-6 max-w-2xl">
              <h3 className="font-semibold text-[#F4F1DE] mb-2">Manual build-stage emails</h3>
              <p className="text-xs text-[#F4F1DE]/60 leading-relaxed">
                The scheduler fires Day 1 / 3 / 5 build-progress emails automatically on every paid
                order. If a batch falls behind (or gets ahead), head to the Orders tab and use the
                <strong className="text-[#E07A5F] mx-1">Send update</strong> dropdown on any row to
                dispatch a stage email on demand. Flags are shared with the scheduler so clicking
                a stage that's already been sent won't double-send.
              </p>
            </div>
          </div>
        )}

        {/* Update Order Modal */}
        {selectedOrder && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-[#121212] border border-white/10 p-6 w-full max-w-md" data-testid="update-order-modal">
              <h3 className="text-xl font-bold text-[#F4F1DE] mb-4">
                Update Order #{selectedOrder.id.slice(0, 8).toUpperCase()}
              </h3>
              
              <div className="space-y-4">
                {/* Order Info */}
                <div className="bg-[#0A0A0A] p-4 rounded text-sm">
                  <p className="text-[#F4F1DE]/60">
                    <span className="font-medium text-[#F4F1DE]">Total:</span> £{selectedOrder.total?.toFixed(2)}
                  </p>
                  <p className="text-[#F4F1DE]/60 mt-1">
                    <span className="font-medium text-[#F4F1DE]">Address:</span><br />
                    {selectedOrder.shipping_address}
                  </p>
                </div>

                {/* Status Select */}
                <div>
                  <label className="text-[#F4F1DE]/80 text-sm block mb-2">Status</label>
                  <select
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                    className="w-full h-10 bg-[#0A0A0A] border border-white/10 text-[#F4F1DE] px-3 rounded"
                  >
                    <option value="pending">Pending</option>
                    <option value="processing">Processing</option>
                    <option value="shipped">Shipped</option>
                    <option value="delivered">Delivered</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>

                {/* Tracking Fields (show when shipped) */}
                {(newStatus === "shipped" || newStatus === "delivered") && (
                  <>
                    <div>
                      <label className="text-[#F4F1DE]/80 text-sm block mb-2">Tracking Number</label>
                      <Input
                        value={trackingNumber}
                        onChange={(e) => setTrackingNumber(e.target.value)}
                        placeholder="e.g., 1234567890"
                        className="bg-[#0A0A0A] border-white/10 text-[#F4F1DE]"
                      />
                    </div>
                    <div>
                      <label className="text-[#F4F1DE]/80 text-sm block mb-2">Courier</label>
                      <select
                        value={courier}
                        onChange={(e) => setCourier(e.target.value)}
                        className="w-full h-10 bg-[#0A0A0A] border border-white/10 text-[#F4F1DE] px-3 rounded"
                      >
                        <option value="">Select courier</option>
                        <option value="DPD">DPD</option>
                        <option value="Parcelforce">Parcelforce</option>
                        <option value="Royal Mail">Royal Mail</option>
                        <option value="DHL">DHL</option>
                        <option value="UPS">UPS</option>
                      </select>
                    </div>
                  </>
                )}

                <div className="flex gap-3 mt-6">
                  <Button
                    variant="outline"
                    onClick={() => setSelectedOrder(null)}
                    className="flex-1 border-white/20 text-[#F4F1DE]/80"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleUpdateStatus}
                    disabled={updating}
                    className="flex-1 bg-[#E07A5F] hover:bg-[#D06A4F] text-white"
                  >
                    {updating ? <span className="spinner" /> : "Update Order"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;

/** Dropdown attached to every Orders row — fires a build-stage email on click. */
const BuildEmailMenu = ({ order, sending, onSend }) => {
  const [open, setOpen] = useState(false);
  const STAGES = [
    { key: "frame_cut", label: "Day 1 · Frame cut", flag: "build_day1_sent" },
    { key: "glass_fitted", label: "Day 3 · Glass fitted", flag: "build_day3_sent" },
    { key: "stained_curing", label: "Day 5 · Stained & curing", flag: "build_day5_sent" },
  ];
  const isEligible = ["paid", "processing"].includes(order.status);
  return (
    <div className="relative">
      <Button
        size="sm"
        variant="outline"
        onClick={() => setOpen(!open)}
        disabled={!isEligible}
        className="border-white/20 text-[#F4F1DE]/70 whitespace-nowrap"
        data-testid={`send-update-${order.id.slice(0,8)}`}
        title={isEligible ? "Send a build-stage email" : "Only available for paid/processing orders"}
      >
        <Send className="w-3.5 h-3.5 mr-1" />
        Send update
      </Button>
      {open && (
        <div
          className="absolute right-0 mt-1 w-56 bg-[#121212] border border-white/10 shadow-2xl z-20"
          onMouseLeave={() => setOpen(false)}
          data-testid={`send-update-menu-${order.id.slice(0,8)}`}
        >
          {STAGES.map((s) => {
            const alreadySent = Boolean(order[s.flag]);
            const key = `${order.id}:${s.key}`;
            const isSending = sending === key;
            return (
              <button
                key={s.key}
                onClick={() => { onSend(order.id, s.key); setOpen(false); }}
                disabled={isSending}
                className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-xs text-[#F4F1DE] hover:bg-[#E07A5F]/10 disabled:opacity-50"
              >
                <span>{s.label}</span>
                {alreadySent && <CheckCircle className="w-3.5 h-3.5 text-green-400" />}
                {isSending && <span className="text-[#E07A5F]">…</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

